/** Feature-scoped adapters; imported only by the surfaces that use them. */
import * as React from "react";
import { useApiClient } from "@authe/ui-core/api";
import { createApiSavedViewsSource } from "@authe/saved-views";
import { createApiExportLedger } from "@authe/export";
import { useCurrentUserId } from "@/app/storageIdentity";

export function useSavedViewsSource() {
  const client = useApiClient();
  // The source needs the caller's id so `setDefault` only clears defaults on
  // rows this user owns — a tenant-shared view in that list can be somebody
  // else's, and the backend rightly refuses to mutate it. Read through the
  // shared accessor so this and dashboardPrefs cannot drift apart.
  const currentUserId = useCurrentUserId();
  // The dashboard is always signed in, so a share link is resolved on the
  // AUTHENTICATED route: a link aimed at named people (an invitation included)
  // admits its reader there and refuses them on the anonymous one with
  // "sign in required" — which is how a targeted publication could never be
  // contributed to from the dashboard.
  return React.useMemo(
    () => createApiSavedViewsSource(client, { currentUserId, authenticatedResolve: true }),
    [client, currentUserId],
  );
}

/**
 * The export ledger, backed by `saved-views-api`'s `/exports` routes.
 *
 * An export is the moment tenant data leaves the platform. Every `<ExportMenu>`
 * under `<DashboardExportLedger>` records one row per export — who, what, from
 * which view, how many rows, under which filters — and a failed export records
 * one too, because "no row appeared" is not an answer an operator can act on.
 *
 * The download never depends on this: `record` swallows its own errors (see
 * `createApiExportLedger`), so an unreachable ledger costs an audit row, never
 * the operator's file.
 */
export function useExportLedgerSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiExportLedger(client), [client]);
}
